import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Spinner } from '../components/Loaders';
import useClients from '../hooks/useClients';
import { Client } from '../pages/dashboard/Clients/types';
import { LocationAwareClientContextProps } from './types';
import { RootState } from '../app/store/store';
import { useSelector } from 'react-redux';
import ClientsContext, { ClientsContextType } from '../context/ClientsContext';

export const LocationAwareClientContext = ({ children }: LocationAwareClientContextProps) => {
  const location = useLocation();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const user = useSelector((state: RootState) => state.Auth.user);

  const { loading, fetchClients } = useClients({
    setClients,
    setClient: setSelectedClient,
  });

  useEffect(() => {
    fetchClients();
  }, [fetchClients, user]);

  useEffect(() => {
    const isClientRoute = location.pathname.match(/^\/[^/]+\/locations/);
    if (!isClientRoute) {
      setSelectedClient(null);
    }
  }, [location.pathname]);

  const clientsContextValue: ClientsContextType = useMemo(
    () => ({
      clients,
      selectedClient,
      addClients: (newClients: Client[], resetClients = false) => {
        setClients(resetClients ? [] : newClients);
      },
      setSelectedClient: (client: Client | null, resetClient = false) => {
        setSelectedClient(resetClient ? null : client);
      },
      removeClient: (clientId: string | number) => {
        setClients(clients.filter(client => client.id !== clientId));
      },
      updateClient: (updatedClient: Client) => {
        setClients(clients.map(client => (client.id === updatedClient.id ? updatedClient : client)));
      },
    }),
    [clients, selectedClient]
  );

  return (
    <ClientsContext.Provider value={clientsContextValue}>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <Spinner color="primary" />
        </div>
      ) : (
        children
      )}
    </ClientsContext.Provider>
  );
};
